import { useState } from 'react';
import type { ArchiveRecord, DraftEmailIntent } from '../types/moderation';
import { draftEmail } from '../lib/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from './ui/Dialog';
import LoadingSpinner from './ui/LoadingSpinner';

interface SuggestReplyProps {
  record: ArchiveRecord;
}

interface IntentMeta {
  intent: DraftEmailIntent;
  label: string;
  blurb: string;
  className: string;
}

const INTENTS: IntentMeta[] = [
  {
    intent: 'clarify',
    label: 'Clarify',
    blurb: 'Ask for missing details',
    className: 'suggest-reply__btn suggest-reply__btn--clarify',
  },
  {
    intent: 'request_more',
    label: 'Request more',
    blurb: 'Ask for related items',
    className: 'suggest-reply__btn suggest-reply__btn--more',
  },
  {
    intent: 'approve',
    label: 'Approve',
    blurb: 'Thank-you note',
    className: 'suggest-reply__btn suggest-reply__btn--approve',
  },
  {
    intent: 'reject',
    label: 'Reject',
    blurb: 'Kind decline',
    className: 'suggest-reply__btn suggest-reply__btn--reject',
  },
];

/**
 * Build a `mailto:` URL with subject + body pre-filled. Encodes per RFC 6068.
 */
export function buildMailtoUrl(
  recipient: string,
  subject: string,
  body: string,
): string {
  const params = new URLSearchParams();
  if (subject) params.set('subject', subject);
  if (body) params.set('body', body);
  // URLSearchParams encodes spaces as '+'; mailto wants %20.
  const qs = params.toString().replace(/\+/g, '%20');
  return `mailto:${encodeURIComponent(recipient)}${qs ? `?${qs}` : ''}`;
}

/**
 * Sidebar widget that drafts a contextual reply email via Bedrock and lets
 * the moderator either open it in their default mail client or copy it to
 * the clipboard. The drafted email is editable before sending.
 */
export default function SuggestReply({ record }: SuggestReplyProps) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [intent, setIntent] = useState<DraftEmailIntent | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [recipient, setRecipient] = useState('');
  const [copied, setCopied] = useState(false);

  const contributorEmail = record.contributor?.email || '';
  const canDraft = Boolean(contributorEmail);

  const requestDraft = async (which: DraftEmailIntent) => {
    setBusy(true);
    setError(null);
    setIntent(which);
    setOpen(true);
    setSubject('');
    setBody('');
    setRecipient('');
    setCopied(false);
    try {
      const result = await draftEmail(record.submissionId, which);
      setSubject(result.subject);
      setBody(result.body);
      setRecipient(result.recipient);
    } catch (err) {
      setError((err as Error).message || 'Failed to draft reply');
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    if (busy) return;
    setOpen(false);
    setIntent(null);
    setError(null);
  };

  const mailtoHref =
    recipient && (subject || body) ? buildMailtoUrl(recipient, subject, body) : '';

  const copyToClipboard = async () => {
    const text = subject ? `Subject: ${subject}\n\n${body}` : body;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers / jsdom: leave the user with a hint
        throw new Error('Clipboard API unavailable');
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setError((err as Error).message || 'Could not copy to clipboard');
    }
  };

  return (
    <aside className="suggest-reply" aria-label="Suggest reply" data-testid="suggest-reply">
      <h3>Suggest reply</h3>
      {!canDraft && (
        <p className="suggest-reply__no-email" data-testid="suggest-reply-no-email">
          Contributor has no email on file.
        </p>
      )}
      {canDraft && (
        <div className="suggest-reply__buttons">
          {INTENTS.map((meta) => (
            <button
              key={meta.intent}
              type="button"
              className={meta.className}
              onClick={() => requestDraft(meta.intent)}
              data-testid={`suggest-reply-${meta.intent}`}
              title={meta.blurb}
            >
              {meta.label}
            </button>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={(o) => !o && close()}>
        {open && (
          <DialogContent>
            <DialogTitle>
              {intent ? `Drafted reply: ${INTENTS.find((i) => i.intent === intent)?.label}` : 'Drafted reply'}
            </DialogTitle>
            <DialogDescription>
              Review and edit before sending. The draft is generated by AI from this submission's metadata.
            </DialogDescription>

            {busy && (
              <div
                className="loading-state"
                data-testid="suggest-reply-loading"
                aria-busy="true"
                role="status"
              >
                <LoadingSpinner inline label="Drafting reply" /> Drafting…
              </div>
            )}

            {!busy && error && (
              <div className="field-error" role="alert" data-testid="suggest-reply-error">
                {error}
              </div>
            )}

            {!busy && !error && (
              <>
                <label className="form-group">
                  <span>To</span>
                  <input
                    type="email"
                    value={recipient}
                    readOnly
                    data-testid="suggest-reply-recipient"
                  />
                </label>
                <label className="form-group">
                  <span>Subject</span>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    data-testid="suggest-reply-subject"
                  />
                </label>
                <label className="form-group">
                  <span>Body</span>
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={12}
                    data-testid="suggest-reply-body"
                  />
                </label>
              </>
            )}

            <div className="dlg-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={close}
                disabled={busy}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={copyToClipboard}
                disabled={busy || !body}
                data-testid="suggest-reply-copy"
              >
                {copied ? 'Copied' : 'Copy to clipboard'}
              </button>
              <a
                className="btn btn-primary"
                href={mailtoHref || '#'}
                onClick={(e) => {
                  if (!mailtoHref) e.preventDefault();
                }}
                aria-disabled={!mailtoHref}
                data-testid="suggest-reply-mailto"
              >
                Open in mail client
              </a>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </aside>
  );
}
